import { Pointer } from '@yleisradio/areena-types';
import classNames from 'classnames';
import React from 'react';
import { Link } from 'components/Link';
import styles from './Button.module.scss';

export type ButtonSize = 'lg' | 'md' | 'sm' | 'xs';
export type ButtonVariant = 'primary' | 'secondary' | 'text';
export type ButtonPadding = 'default' | 'vertical' | 'horizontal' | 'none';

interface ButtonProps {
  text: string;
  iconOnly?: boolean;
  disabled?: boolean;
  download?: boolean;
  iconLeft?: React.ComponentType<React.SVGProps<SVGSVGElement>> | undefined;
  iconRight?: React.ComponentType<React.SVGProps<SVGSVGElement>> | undefined;
  onClick?: React.MouseEventHandler | undefined;
  padding?: ButtonPadding | undefined;
  pointer?: Pointer | undefined;
  size?: ButtonSize | undefined;
  variant?: ButtonVariant | undefined;
  width?: 'full' | undefined;
  'aria-expanded'?: boolean | undefined;
  'aria-controls'?: string | undefined;
  'aria-label'?: string | undefined;
}

export const Button: React.FunctionComponent<ButtonProps> = ({
  text,
  disabled,
  download,
  iconLeft: IconLeft,
  iconOnly,
  iconRight: IconRight,
  onClick,
  padding = 'default',
  pointer,
  size = 'sm',
  variant = 'primary',
  width,
  ...props
}) => {
  const className = classNames(
    styles.button,
    styles[`size-${size}`],
    styles[`variant-${variant}`],
    styles[`padding-${padding}`],
    width && styles[`width-${width}`],
    disabled && styles.disabled,
    iconOnly && styles.iconOnly
  );

  if (pointer && !disabled) {
    return (
      <Link
        className={className}
        download={download}
        onClick={onClick}
        pointer={pointer}
        aria-expanded={props['aria-expanded']}
        aria-controls={props['aria-controls']}
        aria-label={props['aria-label'] || (iconOnly ? text : undefined)}
      >
        {IconLeft && <IconLeft aria-hidden className={styles.icon} />}
        {!iconOnly && <span className={styles.text}>{text}</span>}
        {IconRight && <IconRight aria-hidden className={styles.icon} />}
      </Link>
    );
  }
  return (
    <button
      className={className}
      disabled={disabled || undefined}
      onClick={!disabled ? onClick : undefined}
      aria-expanded={props['aria-expanded']}
      aria-controls={props['aria-controls']}
      aria-label={props['aria-label'] || (iconOnly ? text : undefined)}
    >
      {IconLeft && <IconLeft aria-hidden className={styles.icon} />}
      {!iconOnly && <span className={styles.text}>{text}</span>}
      {IconRight && <IconRight aria-hidden className={styles.icon} />}
    </button>
  );
};
